import {Dispatch, SetStateAction, useEffect} from 'react';

import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {BaseCondition} from 'studies/typed/shared/useExperimentDesignState';

import {getNodeConnectionRoles} from './procedureRunDesignFlowchartConnectionUtils';
import {isProcedureRunDesignFlowchartNode} from './ProcedureRunDesignFlowchartNode';
import type {ProcedureRunDesignFlowchartLayoutEdge} from './useProcedureRunDesignFlowchartEdges';
import type {ProcedureRunDesignFlowchartLayoutNode} from './useProcedureRunDesignFlowchartLayout';

export interface UseProcedureRunDesignFlowchartConnectionRolesArgs {
  conditions: Array<BaseCondition>;
  methods: Array<ExperimentMethod>;
  activeMethodId: string | null;
  edges: Array<ProcedureRunDesignFlowchartLayoutEdge>;
  // Re-seeding signals: when the initial nodes are rebuilt (e.g. conditions/methods/status change),
  // the node store resets showPersistentSourceHandle to its static seed, so we must re-apply the live value.
  initialColumnNodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  initialRows: Array<Array<ProcedureRunDesignFlowchartLayoutNode>>;
  setNodes: Dispatch<SetStateAction<Array<ProcedureRunDesignFlowchartLayoutNode>>>;
}

/**
 * Applies edge-dependent source-handle visibility onto the grid nodes. Returns the same node object
 * when a node's `showPersistentSourceHandle` is unchanged, so re-applying the result can't trigger a layout
 * loop. `connectionRole` is left untouched here: it is static and seeded once when nodes are built.
 */
export function applyShowPersistentSourceHandle(
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>,
  showPersistentSourceHandleByNodeId: ReadonlyMap<string, boolean>
): Array<ProcedureRunDesignFlowchartLayoutNode> {
  return nodes.map((node) => {
    if (!isProcedureRunDesignFlowchartNode(node)) {
      return node;
    }
    const next = showPersistentSourceHandleByNodeId.get(node.id) ?? false;
    return node.data.showPersistentSourceHandle === next
      ? node
      : {...node, data: {...node.data, showPersistentSourceHandle: next}};
  });
}

/**
 * Keeps each grid node's `showPersistentSourceHandle` in sync with the live edges, so drawing an
 * upstream->active edge recomputes handle visibility immediately. No-ops when a node's visibility is
 * unchanged, so it can't trigger a layout loop.
 */
export function useProcedureRunDesignFlowchartConnectionRoles({
  conditions,
  methods,
  activeMethodId,
  edges,
  initialColumnNodes,
  initialRows,
  setNodes,
}: UseProcedureRunDesignFlowchartConnectionRolesArgs): void {
  useEffect(() => {
    const {showPersistentSourceHandleByNodeId} = getNodeConnectionRoles(
      conditions,
      methods,
      edges,
      activeMethodId
    );
    setNodes((current) => applyShowPersistentSourceHandle(current, showPersistentSourceHandleByNodeId));
  }, [conditions, methods, activeMethodId, edges, initialColumnNodes, initialRows, setNodes]);
}
